import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

const statusChip = (status) => {
  if (status === 'ok' || status === 'validated' || status === 'done') {
    return (
      <Chip
        size="small"
        icon={<CheckCircleOutlineIcon />}
        label={status === 'validated' ? 'Validated' : 'Done'}
        color="success"
        variant="outlined"
        sx={{ height: 22 }}
      />
    );
  }
  if (status === 'warn') {
    return (
      <Chip
        size="small"
        icon={<WarningAmberIcon />}
        label="Warning"
        color="warning"
        variant="outlined"
        sx={{ height: 22 }}
      />
    );
  }
  if (status === 'skip') {
    return (
      <Chip
        size="small"
        icon={<RemoveCircleOutlineIcon />}
        label="Skip"
        variant="outlined"
        sx={{ height: 22 }}
      />
    );
  }
  return null;
};

const DestroySectionCard = ({ section }) => {
  const stepLabel = section.step != null ? String(section.step) : '✓';
  const isValidation = section.kind === 'validation' || section.validation_status === 'validated';

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${section.border || '#ccc'}`,
        bgcolor: section.bgcolor || '#fafafa',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          px: 1.5,
          py: 1.25,
          borderBottom: `1px solid ${section.border || '#eee'}`
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: section.color || '#555',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 14,
            flexShrink: 0
          }}
        >
          {stepLabel}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ color: section.color, fontWeight: 700 }}>
              {section.step != null ? `Step ${section.step}: ` : ''}{section.title}
            </Typography>
            {isValidation && (
              <Chip
                size="small"
                color="success"
                label="Validation complete"
                sx={{ height: 22, fontWeight: 600 }}
              />
            )}
          </Box>
          {section.description && (
            <Typography variant="caption" color="text.secondary" display="block">
              {section.description}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ px: 1.5, py: 1 }}>
        {(section.items || []).map((item, idx) => (
          <Box
            key={item.id || `${section.key}-${idx}`}
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              py: 0.75,
              borderBottom: idx < (section.items.length - 1) ? '1px dashed rgba(0,0,0,0.08)' : 'none'
            }}
          >
            <Chip
              size="small"
              label={`${section.step != null ? `${section.step}.` : ''}${idx + 1}`}
              sx={{
                height: 22,
                fontWeight: 700,
                bgcolor: 'rgba(255,255,255,0.7)',
                border: `1px solid ${section.border || '#ddd'}`
              }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {item.label}
                </Typography>
                {statusChip(item.status)}
              </Box>
              {item.detail && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    wordBreak: 'break-word',
                    fontFamily: String(item.detail).includes('/') ? 'monospace' : 'inherit'
                  }}
                >
                  {item.detail}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

/**
 * Color-coded, numbered destroy / backup / rollback plan for Super Admin delete dialog.
 * Seven destroy steps mirror cloud provision (validation steps show “Validation complete”).
 */
const TenantDestroyPlanPanel = ({ preview, backupEnabled }) => {
  if (!preview) return null;

  const destroySections = preview.destroy_sections || [];
  const keptSections = preview.kept_sections || [];
  const backup = preview.backup || {};
  const rollback = backupEnabled ? preview.rollback?.with_backup : preview.rollback?.without_backup;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #ffe082',
          bgcolor: '#fffde7',
          borderRadius: 2,
          p: 1.5
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: '#f9a825',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 13
            }}
          >
            0
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#f57f17', fontWeight: 700 }}>
              Step 0: Backup {backupEnabled ? '(enabled)' : '(skipped — no rollback)'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Runs before destroy when backup is checked
            </Typography>
          </Box>
        </Box>

        {backup.recommended && (
          <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
            Backup recommended: {(backup.reasons || []).join('; ')}
          </Alert>
        )}

        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Where it is stored</Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 0.5 }}>
          Directory: {backup.directory || '—'}
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 0.5 }}>
          File pattern: {backup.file_name_pattern || '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {backup.stored_on}
          {backup.recorded_on_tenant ? ` · ${backup.recorded_on_tenant}` : ''}
        </Typography>
        {(backup.contents || []).map((c) => (
          <Typography key={c} variant="caption" display="block">• {c}</Typography>
        ))}
        {backup.note && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {backup.note}
          </Typography>
        )}
      </Paper>

      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Destroy sequence — 7 steps (mirrors provision)
        {' · '}
        {preview.summary?.total_items || destroySections.reduce((n, s) => n + (s.items?.length || 0), 0)} items
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 380, overflow: 'auto', pr: 0.5 }}>
        {destroySections.map((section) => (
          <DestroySectionCard key={section.key || section.title} section={section} />
        ))}
        {keptSections.map((section) => (
          <DestroySectionCard key={section.key || section.title} section={section} />
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: backupEnabled ? '1px solid #81c784' : '1px solid #ef9a9a',
          bgcolor: backupEnabled ? '#e8f5e9' : '#ffebee',
          borderRadius: 2,
          p: 1.5
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: backupEnabled ? '#2e7d32' : '#c62828', mb: 0.5 }}
        >
          Rollback / revert destroy
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {rollback?.summary || (backupEnabled
            ? 'With backup you can restore later.'
            : 'Without backup, destroy cannot be rolled back from TenantHub.')}
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>How to revert</Typography>
        {(rollback?.how_to || []).map((line) => (
          <Typography key={line} variant="caption" display="block" sx={{ mb: 0.25 }}>
            {line}
          </Typography>
        ))}
        {(rollback?.limitations || []).length > 0 && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1, mb: 0.5 }}>
              Limitations
            </Typography>
            {(rollback.limitations || []).map((line) => (
              <Typography key={line} variant="caption" display="block" color="text.secondary">
                • {line}
              </Typography>
            ))}
          </>
        )}
      </Paper>

      {(preview.warnings || []).map((w) => (
        <Typography key={w} variant="caption" color="text.secondary" display="block">• {w}</Typography>
      ))}
    </Box>
  );
};

export default TenantDestroyPlanPanel;
